export const ENGAGEMENT_TYPES = [
    { v: "prep_only", label: "Preparation only", short: "Prep", desc: "We prepare the service charge accounts. No independent review, no report of factual findings." },
    { v: "audit_only", label: "Audit only", short: "Audit", desc: "ISA (UK) 800 audit of service charge accounts prepared by the managing agent." },
    { v: "review_only", label: "ROFF only", short: "ROFF", desc: "TECH 03/11 review of accounts prepared by the managing agent, with report of factual findings (ROFF)." },
    { v: "prep_review", label: "Prep & ROFF", short: "Prep + ROFF", desc: "We prepare the accounts and perform the TECH 03/11 review procedures, issuing a report of factual findings (ROFF)." },
    { v: "prep_audit", label: "Preparation + Audit", short: "Prep + Audit", desc: "We prepare the service charge accounts and perform the ISA (UK) 800 independent audit — prep and audit responsibilities are separated across the team (Preparer, Auditor, Manager, Partner)." },
];
export const typeLabel = (v) => ENGAGEMENT_TYPES.find((t) => t.v === v)?.label || v;
export const typeShort = (v) => ENGAGEMENT_TYPES.find((t) => t.v === v)?.short || v;

export const SECTION_NAMES = {
    A: "Planning & Acceptance",
    B: "Opening Balances",
    C: "Income & Debtors",
    D: "Expenditure & Creditors",
    E: "Bank, Reserves & Journals",
    F: "Finalisation",
    G: "Deliverables & Reporting",
};

export const LEAD_CODES = [
    { v: "C1", label: "C1 · SC Income (I&E)" },
    { v: "D1", label: "D1 · Expenses (I&E)" },
    { v: "C2", label: "C2 · Debtors" },
    { v: "C3", label: "C3 · SC in advance" },
    { v: "C10", label: "C10 · Receivables & other CA" },
    { v: "C11", label: "C11 · Other current assets" },
    { v: "D2", label: "D2 · Accruals & prepayments" },
    { v: "D3", label: "D3 · Creditors" },
    { v: "D7", label: "D7 · Payables & other CL" },
    { v: "D8", label: "D8 · Other current liabilities" },
    { v: "E1", label: "E1 · Bank" },
    { v: "E2", label: "E2 · Reserves" },
];

export const QUERY_TYPES = {
    client: { label: "Client", cls: "bg-sky-100 text-sky-800 border-sky-200" },
    internal: { label: "Internal", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    review: { label: "Review", cls: "bg-rose-100 text-rose-800 border-rose-200" },
    attention: { label: "Attention", cls: "bg-orange-100 text-orange-800 border-orange-300" },
};

export function fmtGbp(n) {
    if (n === undefined || n === null || isNaN(n)) return "—";
    if (Math.abs(n) < 0.005) return "—";
    const v = Math.abs(n);
    return `${n < 0 ? "(" : ""}£${v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${n < 0 ? ")" : ""}`;
}
export function fmtGbp0(n) {
    if (n === undefined || n === null || isNaN(n)) return "—";
    if (Math.round(n) === 0) return "—";
    const v = Math.abs(Math.round(n));
    return `${n < 0 ? "(" : ""}£${v.toLocaleString("en-GB", { maximumFractionDigits: 0 })}${n < 0 ? ")" : ""}`;
}
export function fmtNum(n) {
    if (n === undefined || n === null || isNaN(n)) return "—";
    if (Math.abs(n) < 0.005) return "—";
    const v = Math.abs(n);
    return `${n < 0 ? "(" : ""}${v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${n < 0 ? ")" : ""}`;
}
export function fmtNum0(n) {
    if (n === undefined || n === null || isNaN(n)) return "—";
    if (Math.round(n) === 0) return "—";
    const v = Math.abs(Math.round(n));
    return `${n < 0 ? "(" : ""}${v.toLocaleString("en-GB", { maximumFractionDigits: 0 })}${n < 0 ? ")" : ""}`;
}
export function variance(a, b) {
    if (!b) return 0;
    return ((a - b) / Math.abs(b)) * 100;
}
export function fmtPct(v) {
    if (!isFinite(v) || v === 0) return "—";
    return `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
}
export function fmtPct0(v) {
    if (!isFinite(v) || v === 0) return "—";
    return `${v > 0 ? "+" : ""}${Math.round(v)}%`;
}

export function fmtLongDate(iso) {
    if (!iso) return "";
    const s = String(iso).slice(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return s;
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${parseInt(m[3], 10)} ${months[parseInt(m[2], 10) - 1]} ${m[1]}`;
}

// Resolve the heading text for a note table's description / current / prior columns.
// Per-table overrides win; otherwise "dates" mode shows the period-end dates, else the
// engagement's generic column labels. The "(£)" suffix is added by the renderer.
export function noteTableHeaders(tb, labels, periodEnds) {
    const L = labels || { current: "Current Period", prior: "Prior Period" };
    const pe = periodEnds || {};
    const dates = (tb && tb.heading_mode) === "dates";
    const ov = (v) => (typeof v === "string" && v.trim() ? v.trim() : "");
    const cur = ov(tb && tb.current_header) || (dates && pe.current ? fmtLongDate(pe.current) : L.current);
    const pri = ov(tb && tb.prior_header) || (dates && pe.prior ? fmtLongDate(pe.prior) : L.prior);
    return { desc: ov(tb && tb.desc_header), current: cur, prior: pri };
}

export const FILE_STATUS = {
    planning: { label: "Planning", cls: "bg-slate-100 text-slate-700" },
    fieldwork: { label: "Fieldwork", cls: "bg-blue-50 text-blue-800" },
    awaiting_review: { label: "Awaiting review", cls: "bg-amber-50 text-amber-800" },
    review_points: { label: "Review queries to address", cls: "bg-rose-50 text-rose-800" },
    partner_review: { label: "Partner review", cls: "bg-indigo-50 text-indigo-800" },
    approved: { label: "Approved — awaiting final", cls: "bg-teal-50 text-teal-800" },
    awaiting_client_approval: { label: "Awaiting client approval", cls: "bg-violet-50 text-violet-800" },
    finalised: { label: "Finalised", cls: "bg-emerald-50 text-emerald-800" },
};
